import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BedDoubleIcon, PlusIcon } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { StatCard } from '../components/ui/StatCard';
import { wards, type Bed } from '../data/operations';
const statusConfig: Record<Bed['status'], {
  cls: string;
  label: string;
  dot: string;
}> = {
  Occupied: {
    cls: 'border-slate-200 bg-white',
    label: 'Occupied',
    dot: 'bg-slate-400'
  },
  Vacant: {
    cls: 'border-emerald-200 bg-emerald-50',
    label: 'Vacant',
    dot: 'bg-emerald-500'
  },
  Cleaning: {
    cls: 'border-sky-200 bg-sky-50',
    label: 'Cleaning',
    dot: 'bg-sky-500'
  },
  Maintenance: {
    cls: 'border-amber-200 bg-amber-50',
    label: 'Maintenance',
    dot: 'bg-amber-500'
  },
  Reserved: {
    cls: 'border-violet-200 bg-violet-50',
    label: 'Reserved',
    dot: 'bg-violet-500'
  }
};
const acuityConfig = {
  stable: 'bg-emerald-500',
  guarded: 'bg-amber-500',
  critical: 'bg-red-500'
} as const;
function BedTile({
  bed
}: {
  bed: Bed;
}) {
  const cfg = statusConfig[bed.status];
  return <motion.button initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} className={`flex flex-col rounded-lg border p-2.5 text-left transition-shadow hover:shadow-card ${cfg.cls}`} data-id="element-479" data-mp="1x2dhau2s1szc">
      <div className="flex items-center justify-between" data-id="element-480" data-mp="2701cfl4ap2i1">
        <span className="text-xs font-bold text-slate-700" data-id="element-481" data-mp="26ygj1ytpvplr">{bed.label}</span>
        {bed.acuity && <span className={`h-2 w-2 rounded-full ${acuityConfig[bed.acuity]}`} title={bed.acuity} data-id="element-482" data-mp="21zvsys86imb1" />}
      </div>
      {bed.patient ? <>
          <span className="mt-1.5 truncate text-sm font-medium text-slate-900" data-id="element-483" data-mp="2br9pcr4u35zy">{bed.patient}</span>
          <span className="text-[11px] text-slate-500" data-id="element-484" data-mp="2pqj03ba9t7wj">{bed.los ? `LOS ${bed.los}` : cfg.label}</span>
        </> : <>
          <span className={`mt-1.5 inline-flex items-center gap-1 text-xs font-medium ${bed.status === 'Vacant' ? 'text-emerald-700' : 'text-slate-600'}`} data-id="element-485" data-mp="2ku10eptsrzr2">
            <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} data-id="element-486" data-mp="1nd3e93giqjuv" /> {cfg.label}
          </span>
          <span className="text-[11px] text-slate-400 h-4" data-id="element-487" data-mp="1rf821lkacm9l"></span>
        </>}
    </motion.button>;
}
export function BedBoard() {
  const navigate = useNavigate();
  const all = wards.flatMap(w => w.beds);
  const occupied = all.filter(b => b.status === 'Occupied').length;
  const vacant = all.filter(b => b.status === 'Vacant').length;
  const pct = Math.round(occupied / all.length * 100);
  return <div className="p-6" data-id="element-488" data-mp="2b4sa2tsjtqgk">
      <PageHeader title="IPD Bed Management Board" subtitle="Real-time ward → room → bed occupancy with acuity and transfer workflow" action={<Button icon={<PlusIcon className="h-4 w-4" data-id="element-491" data-mp="gqrbma0wv5h0" />} onClick={() => navigate('/admissions/new')} data-id="element-490" data-mp="gq7q6jc1xyy8">Admit patient</Button>} data-id="element-489" data-mp="2b48u3imglc56" />

      <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4" data-id="element-492" data-mp="krb9jftb6d47">
        <StatCard label="Occupancy" value={`${pct}%`} icon={<BedDoubleIcon className="h-5 w-5" data-id="element-494" data-mp="d9d3jlac5k5q" />} accent="text-brand-600 bg-brand-50" footnote={`${occupied} of ${all.length} shown`} data-id="element-493" data-mp="d8k4l6hmbyy5" />
        <StatCard label="Vacant beds" value={String(vacant)} icon={<BedDoubleIcon className="h-5 w-5" data-id="element-496" data-mp="3fmikqdf3rus" />} accent="text-emerald-600 bg-emerald-50" footnote="Ready for admission" data-id="element-495" data-mp="3h7d4fzqfasn" />
        <StatCard label="Avg LOS" value="4.2 d" icon={<BedDoubleIcon className="h-5 w-5" data-id="element-498" data-mp="w5373cxx2olq" />} accent="text-sky-600 bg-sky-50" footnote="Rolling 30 days" data-id="element-497" data-mp="w6e7d2s5le83" />
        <StatCard label="Forecast discharges" value="3" icon={<BedDoubleIcon className="h-5 w-5" data-id="element-500" data-mp="n5g5aq19zgs5" />} accent="text-violet-600 bg-violet-50" footnote="Next 24h" data-id="element-499" data-mp="r7t8p7sjv2lr" />
      </div>

      {/* Legend */}
      <div className="mb-4 flex flex-wrap gap-4 text-xs text-slate-600" data-id="element-501" data-mp="i5tqwd9pnuhr">
        {Object.entries(statusConfig).map(([k, v]) => <span key={k} className="inline-flex items-center gap-1.5" data-id="element-502" data-mp="37tugd38j9utq">
            <span className={`h-2.5 w-2.5 rounded-full ${v.dot}`} data-id="element-503" data-mp="32v9cmtu2ot0j" /> {v.label}
          </span>)}
        <span className="ml-auto hidden sm:inline-flex items-center gap-3 text-slate-500" data-id="element-504" data-mp="uml48akny21m">
          <span className="inline-flex items-center gap-1.5" data-id="element-505" data-mp="zfmhke581tj3"><span className="h-2 w-2 rounded-full bg-emerald-500" data-id="element-506" data-mp="lnpvoru5cfv5" />Stable</span>
          <span className="inline-flex items-center gap-1.5" data-id="element-507" data-mp="llmvjeza56cp"><span className="h-2 w-2 rounded-full bg-amber-500" data-id="element-508" data-mp="pq309cx5q7qc" />Guarded</span>
          <span className="inline-flex items-center gap-1.5" data-id="element-509" data-mp="1de43q7fhaxyw"><span className="h-2 w-2 rounded-full bg-red-500" data-id="element-510" data-mp="1dff6q513o1oy" />Critical</span>
        </span>
      </div>

      <div className="space-y-4" data-id="element-511" data-mp="1icxhm0yogqzl">
        {wards.map(ward => {
        const occ = ward.beds.filter(b => b.status === 'Occupied').length;
        return <Card key={ward.name} className="overflow-hidden" data-id="element-512" data-mp="19bzctggf0ffy">
              <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3" data-id="element-513" data-mp="3p46hjzrd7fz2">
                <div data-id="element-514" data-mp="3u2zu9pytv2y6">
                  <h3 className="text-sm font-semibold text-slate-900" data-id="element-515" data-mp="3u3ja914x3h9k">{ward.name}</h3>
                  <p className="text-xs text-slate-500" data-id="element-516" data-mp="3llppln1vwshd">{ward.type} · {occ}/{ward.beds.length} occupied</p>
                </div>
                <div className="h-1.5 w-28 overflow-hidden rounded-full bg-slate-100" data-id="element-517" data-mp="3hjvrnmj2q2kw">
                  <div className="h-full rounded-full bg-brand-500" style={{
                width: `${occ / ward.beds.length * 100}%`
              }} data-id="element-518" data-mp="3vjdlwhf5281n" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2.5 p-4 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8" data-id="element-519" data-mp="32vraimzo5z48">
                {ward.beds.map(b => <BedTile key={b.id} bed={b} data-id="element-520" data-mp="2xxzjrwargj1m" />)}
              </div>
            </Card>;
      })}
      </div>
    </div>;
}